import { useCallback, useState } from 'react';

import { useHousehold } from '../contexts/HouseholdContext';
import { useLiabilities } from '../contexts/LiabilityContext';
import { usePortfolio } from '../contexts/PortfolioContext';
import { notifyDashboardRefresh } from '../services/dashboardRefreshBus';

/**
 * Manual full-app refresh: analytics, insights, portfolio, liabilities, household.
 * Widgets subscribed to `dashboardRefreshBus` refetch when this runs.
 */
export function useAppRefresh(): {
  refreshAll: (reason?: string) => Promise<void>;
  refreshing: boolean;
} {
  const { refresh: refreshHousehold } = useHousehold();
  const { refresh: refreshPortfolio } = usePortfolio();
  const { refresh: refreshLiabilities } = useLiabilities();
  const [refreshing, setRefreshing] = useState(false);

  const refreshAll = useCallback(
    async (reason = 'manual') => {
      if (refreshing) return;
      setRefreshing(true);
      try {
        // Bus notifies analytics / insights / spend; await the three shared
        // stores (single-flight inside each so this is not a double fetch).
        notifyDashboardRefresh(reason);
        await Promise.all([
          refreshHousehold(),
          refreshPortfolio(),
          refreshLiabilities(),
        ]);
      } catch (error) {
        console.error('[useAppRefresh] ❌ Refresh failed', {
          reason,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setRefreshing(false);
      }
    },
    [refreshHousehold, refreshLiabilities, refreshPortfolio, refreshing],
  );

  return { refreshAll, refreshing };
}
